import { ApiConfigs, KeyQueryApi } from "../config/apiConfig";
import { PostTypeKey } from "../types/common.type";
import apiClient from "../services/api/apiClient";
import { ItemListProduct } from "../types/post.type";
import { useInfiniteQuery, InfiniteData } from "@tanstack/react-query";
import axios from "axios";
import React from "react";

// Generic interface for paginated API responses
export interface ListPostResponse<T> {
  data: T[];
  // paged: number;
  page: number;
  is_next: boolean;
}

export const fetchListPost = async (
  api: string,
  type: string,
  params: paramsGetListInterface
): Promise<ListPostResponse<ItemListProduct>> => {
  console.log("API endpoint:", api , "Type:", type, "Params:", params);
  // Make sure api is not empty before appending query parameters
  if (!api) {
    throw new Error("API endpoint is missing");
  }

  // Remove trailing slash if present to normalize the URL
  const baseApi = api.endsWith("/") ? api.slice(0, -1) : api;

  // Build URL with path-based pagination
  // Format should be: https://phim3s.net/vi/api/v1/posts/lists/movie/chinh-kich/paged/2/?sortby=views_day__desc
  // Note: paged is now part of the URL path, not a query parameter
  // For page 1, we don't include /paged/1 segment
  const sortbyParam = params.sortby || "";
  // Ensure paged is always a valid number
  const pagedParam = typeof params.paged === 'number' && !isNaN(params.paged) ? params.paged : 1;
  
  // Ensure we include type in the URL if it's provided
 
  
  // Only include paged segment if page > 1
  const pagedSegment = pagedParam > 1 ? `/paged/${pagedParam}` : "";
  
  // Build the final URL
  const url = `${baseApi}${pagedSegment}/?sortby=${sortbyParam}`;

  const response = await axios.get(url,{
    baseURL:ApiConfigs.baseURL,
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
  }).then((res) => {
    // Log the response for debugging
    console.log("API Response:", res.data);
    return res.data;
  }).catch((error) => {
    console.error("Error fetching data:", error);
    throw error;
  });
  
  // Add debug logs to check response data
  console.log("API Response:", {
    is_next: response.data.is_next,
    current_page: response.data.paged,
    items_count: response.data.data?.length,
    url: url,
    pageFormat: pagedParam > 1 ? `Có /paged/${pagedParam}` : "Không có /paged"
  });
  
  return response.data as ListPostResponse<ItemListProduct>;
};

export interface paramsGetListInterface {
  sortby?: string;
  paged?: number;
}

// Hook return interface
export interface UseListPostReturn {
  posts: ItemListProduct[];
  isLoading: boolean;           // loading initial data
  isError: boolean;             // error flag
  error: unknown;               // error object
  hasNextPage: boolean;         // indicates more pages
  isFetchingNextPage: boolean;  // loading more pages
  fetchNextPage: () => void;    // load next page
  refetch: () => void;          // refetch all data
}

export const useListPost = (
  api: string,
  type: PostTypeKey,
  params: paramsGetListInterface
): UseListPostReturn & { isLoadmore: boolean } => {
  // State để kiểm soát loadmore
  const [isLoadmore, setIsLoadmore] = React.useState(false);

  // Paginate list posts with infiniteQuery — two-generic overload
  const infiniteQuery = useInfiniteQuery<
    ListPostResponse<ItemListProduct>, // type returned by each fetch
    Error                             // error type
  >({
    queryKey: [KeyQueryApi.GET_LIST_POST, api, params.sortby, type],
    queryFn: async ({ pageParam }) => {
      // Ensure pageParam is always a valid number
      const page = typeof pageParam === 'number' && !isNaN(pageParam) ? pageParam : 1;
      // Nếu page > 1 thì đang loadmore
      if (page > 1) setIsLoadmore(true);
      else setIsLoadmore(false);
      try {
        // Return a promise to ensure proper async handling
        const result = await fetchListPost(api, type, { sortby: params.sortby, paged: page });
        setIsLoadmore(false);
        return result;
      } catch (error) {
        setIsLoadmore(false);
        throw error;
      }
    },
    initialPageParam: 1, // Always start with page 1
    getPreviousPageParam: (firstPage) => {
      // Support backward pagination if needed
      return firstPage.page > 1 ? firstPage.page - 1 : undefined;
    },
    getNextPageParam: (lastPage) => {
      // Log detailed information about lastPage for debugging
      console.log("Getting next page param from:", {
        is_next: lastPage.is_next,
        current_page: lastPage.page,
        data_length: lastPage.data?.length || 0
      });
      
      // Only increment page when there's actual next page data
      if (lastPage.is_next === true) {
        // Get the current page and ensure it's a valid number
        const currentPage = typeof lastPage.page === 'number' && !isNaN(lastPage.page) 
          ? lastPage.page
          : 1;
        
        // Calculate and return next page number
        const nextPage = currentPage + 1;
        console.log(`Will fetch next page: ${nextPage}`,lastPage);
        return nextPage;
      }
      
      // If no more pages, return undefined to stop pagination
      console.log("No more pages available");
      return undefined;
    },
    // disable auto-refetch
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!api,
  });

  // Combine paginated pages data into one array
  // Cast data to InfiniteData to access pages safely
  const data = infiniteQuery.data as InfiniteData<ListPostResponse<ItemListProduct>> | undefined;
  const pages = data?.pages || [];
  const posts: ItemListProduct[] = pages.flatMap((page) => page.data);
  return {
    posts,
    isLoading: infiniteQuery.isLoading,
    isError: infiniteQuery.isError,
    error: infiniteQuery.error,
    hasNextPage: infiniteQuery.hasNextPage ?? false,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    fetchNextPage: () => {
      setIsLoadmore(true);
      infiniteQuery.fetchNextPage();
    },
    refetch: infiniteQuery.refetch,
    isLoadmore,
  };
};
